import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

import { Vendor } from "@features/catalog/vendor/models";

import { VendorValues } from "@app/features/catalog/client/models";

export type BulkUpdateType = "pricing" | "vendor" | "sla";

export interface BulkUpdateModalData {
  productSubtypes: string[] | null;
  vendors?: Vendor[];
  type: BulkUpdateType;
}

export interface BulkUpdatePricingResult {
  productSubtype: string;
  newPrice: number | null;
  updatePrice: number | null;
}

export interface BulkUpdateVendorResult {
  productSubtype: string;
  vendorValues: VendorValues[];
}

export interface BulkUpdateSlaResult {
  contract: number | null;
  target: number | null;
}

export type BulkUpdateModalResult =
  | BulkUpdatePricingResult
  | BulkUpdateVendorResult
  | BulkUpdateSlaResult;

@Component({
  selector: "app-bulk-update-modal",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
  ],
  templateUrl: "./bulk-update-modal.component.html",
  styleUrl: "./bulk-update-modal.component.scss",
})
export class BulkUpdateModalComponent {
  private readonly fb = inject(FormBuilder);
  readonly dialogRef = inject(MatDialogRef<BulkUpdateModalComponent>);
  readonly data = inject<BulkUpdateModalData>(MAT_DIALOG_DATA);

  readonly form: FormGroup = this.createForm();

  get isPricing(): boolean {
    return this.data.type === "pricing";
  }

  get isVendor(): boolean {
    return this.data.type === "vendor";
  }

  get isSla(): boolean {
    return this.data.type === "sla";
  }

  get vendors(): Vendor[] {
    return this.data.vendors || [];
  }

  private createForm(): FormGroup {
    if (this.data.type === "pricing") {
      return this.fb.group({
        productSubtype: ["", Validators.required],
        newPrice: [null],
        updatePrice: [null],
      });
    } else if (this.data.type === "vendor") {
      const vendorControls: { [key: string]: any } = {
        productSubtype: ["", Validators.required],
      };

      this.vendors.forEach((vendor) => {
        vendorControls[`vendor_${vendor.vendorID}`] = [
          null,
          [Validators.min(0), Validators.max(100)],
        ];
      });

      return this.fb.group(vendorControls);
    } else {
      return this.fb.group({
        contract: [null, [Validators.min(1)]],
        target: [null, [Validators.min(1)]],
      });
    }
  }

  getVendorControlName(vendorId: number): string {
    return `vendor_${vendorId}`;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onApply(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const result = this.buildResult();
    this.dialogRef.close(result);
  }

  private buildResult(): BulkUpdateModalResult {
    switch (this.data.type) {
      case "pricing":
        return this.buildPricingResult();
      case "vendor":
        return this.buildVendorResult();
      default:
        return this.buildSlaResult();
    }
  }

  private buildPricingResult(): BulkUpdatePricingResult {
    return {
      productSubtype: this.form.value.productSubtype,
      newPrice: this.parseNullableNumber(this.form.value.newPrice),
      updatePrice: this.parseNullableNumber(this.form.value.updatePrice),
    };
  }

  private buildVendorResult(): BulkUpdateVendorResult {
    const vendorValues = this.vendors
      .map((vendor) => {
        const value =
          this.form.value[this.getVendorControlName(vendor.vendorID)];
        return this.isValidValue(value)
          ? {
              vendorId: vendor.vendorID,
              vendorName: vendor.vendorName,
              percentage: Number.parseFloat(value),
            }
          : null;
      })
      .filter((v): v is VendorValues => v !== null);

    return {
      productSubtype: this.form.value.productSubtype,
      vendorValues,
    };
  }

  private buildSlaResult(): BulkUpdateSlaResult {
    return {
      contract: this.parseNullableNumber(this.form.value.contract),
      target: this.parseNullableNumber(this.form.value.target),
    };
  }

  private parseNullableNumber(value: string | number | null): number | null {
    return this.isValidValue(value) ? Number.parseFloat(String(value)) : null;
  }

  private isValidValue(value: unknown): boolean {
    return value !== null && value !== "";
  }

  get canApply(): boolean {
    const hasProductSubtype = !!this.form.value.productSubtype;

    if (this.data.type === "pricing") {
      const hasAtLeastOnePrice =
        (this.form.value.newPrice !== null &&
          this.form.value.newPrice !== "") ||
        (this.form.value.updatePrice !== null &&
          this.form.value.updatePrice !== "");
      return this.form.valid && hasProductSubtype && hasAtLeastOnePrice;
    } else if (this.data.type === "vendor") {
      const hasAtLeastOneVendor = this.vendors.some((vendor) => {
        const controlName = this.getVendorControlName(vendor.vendorID);
        const value = this.form.value[controlName];
        return value !== null && value !== "";
      });
      return this.form.valid && hasProductSubtype && hasAtLeastOneVendor;
    } else {
      const hasAtLeastOneValue =
        (this.form.value.contract !== null &&
          this.form.value.contract !== "") ||
        (this.form.value.target !== null && this.form.value.target !== "");
      return this.form.valid && hasAtLeastOneValue;
    }
  }
}
